import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { localeLabels } from '@sonasi/shared-types';

import { CloseButton, Overlay } from '@/components/Overlay';
import { Glyph, ToastLine } from '@/components/Translating';
import { useAiProvider } from '@/lib/ai-provider';
import { saveMessage } from '@/lib/apps';
import {
  missingLocales,
  slugify,
  topicName,
  TOPIC_LOCALES,
  useAdminTopics,
  useCreateTopic,
  useDeleteTopic,
  useTranslateTopics,
  useUpdateTopic,
  type AdminTopic,
  type TopicLocale,
  type TopicTranslation,
} from '@/lib/topics';

/**
 * The topics posts are filed under, in every language the website speaks.
 *
 * Built to the design's second version of this dialog: one row per topic
 * with a chip per language saying whether that language has a name, the row
 * opened to edit the four names in place, and one button at the top that
 * asks the model for whatever is still missing. Every change goes to the
 * server as it is made — a name when its field is left, a topic when Add is
 * pressed — so there is nothing to Save and no Save button, which is the
 * choice the previous version of this file made and the design confirms.
 *
 * Two things the design draws that this does differently, on purpose:
 * deleting a topic that still has posts asks where they should go instead of
 * refusing (the design's own hint says it "asks you to move those posts
 * first"); and a translation can be blanked to be redone, which the design
 * has no gesture for.
 */

export function TopicsModal({ onClose }: { onClose: () => void }) {
  const topics = useAdminTopics();
  const create = useCreateTopic();
  const translate = useTranslateTopics();
  const provider = useAiProvider();
  /**
   * Whether a run could do anything. Read from the provider row the
   * Connections screen already shows, so a studio that has not set one up
   * is not sent a request per Add that the server refuses with a 400, and
   * is not shown a red mark after a topic that was added perfectly well.
   * Unknown (still loading, or the query failed) counts as ready: the
   * server's own answer is then the message, and it is the accurate one.
   */
  const canTranslate = provider.data ? provider.data.configured : true;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; ok: boolean } | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // A name half-typed is saved on the way out, the same as clicking the
      // backdrop would — that click blurs the field; a key does not.
      (document.activeElement as HTMLElement | null)?.blur?.();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.ok ? 3200 : 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  const rows = topics.data ?? [];
  const missing = rows.reduce((sum, topic) => sum + missingLocales(topic.name).length, 0);
  const summary = `${rows.length} ${rows.length === 1 ? 'topic' : 'topics'} · ${missing} ${
    missing === 1 ? 'translation' : 'translations'
  } missing`;

  /**
   * Fills what is missing — for every topic, or for one just added.
   *
   * The outcome is one sentence: how many names landed, and which languages
   * came back with nothing and why. A language failing is not the run
   * failing; the others have already been written by the time this reads.
   */
  const fill = (ids: string[] | undefined, prefix: string) => {
    translate.mutate(ids, {
      onSuccess: (result) => setToast(outcome(result, prefix)),
      onError: (error) => setToast({ message: `${prefix}${saveMessage(error)}`, ok: false }),
    });
  };

  const editName = (value: string) => {
    setName(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const add = () => {
    setAddError(null);
    create.mutate(
      { name: name.trim(), slug: slug.trim() },
      {
        onSuccess: (stored) => {
          // Cleared only once it is stored. Clearing on submit would wipe what
          // somebody typed the moment the server refused the slug.
          setName('');
          setSlug('');
          setSlugEdited(false);
          setOpen(stored.id);
          /*
           * The design's flow: a topic is added and its translations are
           * asked for at once, so the three chips beside it do not sit
           * "missing" until somebody remembers the button. The add has
           * already succeeded — without a provider the topic stays and the
           * toast says where to set one up, as information, not a failure.
           */
          const added = `Topic “${topicName(stored.name)}” added`;
          if (canTranslate) fill([stored.id], `${added} · `);
          else
            setToast({
              message: `${added} · set up an AI provider in Settings › Connections to translate it`,
              ok: true,
            });
        },
        onError: (error) => setAddError(saveMessage(error)),
      },
    );
  };

  const canAdd = name.trim().length > 0 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim());

  return (
    <Overlay onBackdropClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Topics"
        className="flex max-h-[92vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[14px] border"
        style={{
          background: 'var(--panel)',
          borderColor: 'var(--line2)',
          boxShadow: '0 30px 70px -30px rgba(0, 0, 0, 0.7)',
        }}
      >
        <div
          className="flex flex-none items-center gap-2.5 border-b px-4 py-[13px]"
          style={{ borderColor: 'var(--line)' }}
        >
          {/*
            The title takes the slack, so the button sits against the × as the
            design draws it. `ml-auto` on the button would split the space with
            the ×'s own `ml-auto` and leave the button floating mid-header.
          */}
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[13.5px] font-semibold tracking-[-0.01em]">Topics</span>
            <span className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
              {topics.isPending ? 'loading…' : summary}
            </span>
          </span>
          {/*
            "All translated" is a disabled button rather than nothing: the
            person who just filled the last name wants to see that it is
            done, in the place they pressed.
          */}
          <button
            type="button"
            onClick={() => fill(undefined, '')}
            disabled={translate.isPending || topics.isPending || missing === 0}
            className="focus-ring inline-flex min-h-7 flex-none items-center gap-1.5 rounded-[7px] border px-2.5 py-[5px] text-[11.5px] font-medium disabled:cursor-default"
            style={{
              cursor:
                translate.isPending || topics.isPending || missing === 0 ? 'default' : 'pointer',
              background: translate.isPending ? 'var(--accent-soft)' : 'var(--panel2)',
              color: translate.isPending
                ? 'var(--accent)'
                : missing > 0
                  ? 'var(--text)'
                  : 'var(--dim)',
              borderColor: translate.isPending ? 'var(--accent)' : 'var(--line2)',
            }}
          >
            <Glyph spinning={translate.isPending} size={12} />
            {translate.isPending
              ? 'Translating…'
              : topics.isPending
                ? 'Translate'
                : missing > 0
                  ? `Translate ${missing} missing`
                  : 'All translated'}
          </button>
          <CloseButton onClick={onClose} />
        </div>

        <div className="overflow-y-auto px-4 pt-1 pb-3.5">
          {topics.isError ? (
            <p className="m-0 mb-3 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
              {saveMessage(topics.error)}
            </p>
          ) : null}

          <div data-rows>
            {rows.map((topic) => (
              <TopicRow
                key={topic.id}
                topic={topic}
                others={rows.filter((one) => one.id !== topic.id)}
                open={open === topic.id}
                onToggle={() => setOpen((current) => (current === topic.id ? null : topic.id))}
              />
            ))}
            {!topics.isPending && rows.length === 0 ? (
              <p className="m-0 py-4 text-[13px]" style={{ color: 'var(--muted)' }}>
                No topics yet. The first one you add is what posts can be filed under.
              </p>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <input
              aria-label="New topic name"
              value={name}
              placeholder="New topic name"
              onChange={(event) => editName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canAdd && !create.isPending) add();
              }}
              className="field min-h-8 min-w-0 flex-[1_1_160px]"
            />
            {/*
              Follows the name until somebody edits it, and then stops for
              good — the same latch `NewAppModal` uses, and for the same
              reason: correcting the slug and then touching the name would
              otherwise throw the correction away.
            */}
            <input
              aria-label="New topic slug"
              value={slug}
              placeholder="slug"
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(event.target.value);
              }}
              className="field field-mono min-h-8 min-w-0 flex-[0_1_110px]"
            />
            <button
              type="button"
              onClick={add}
              disabled={!canAdd || create.isPending}
              className="btn-accent focus-ring min-h-8 cursor-pointer rounded-[8px] px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
            >
              {create.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
          {addError ? (
            <span
              className="mt-2 block text-xs leading-[1.55]"
              role="alert"
              style={{ color: 'var(--danger)' }}
            >
              {addError}
            </span>
          ) : null}
          <span className="mt-2.5 block text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
            Translate fills only the empty language fields. Deleting a topic that still has posts
            asks you to move those posts first.
          </span>
        </div>
      </div>
      {/*
        Portalled past the overlay, as the app editor's is: the sheet has a
        backdrop filter, which makes it the containing block for anything
        `fixed` inside it.
      */}
      {toast
        ? createPortal(<ToastLine message={toast.message} ok={toast.ok} />, document.body)
        : null}
    </Overlay>
  );
}

/** The toast after a run: names filled, then each language that failed with its reason. */
const outcome = (result: TopicTranslation, prefix: string): { message: string; ok: boolean } => {
  const filled =
    result.filled === 0
      ? 'nothing to translate'
      : `${result.filled} ${result.filled === 1 ? 'translation' : 'translations'} filled`;
  const failed = result.failed.map(
    (one) => `${localeLabels[one.locale as TopicLocale] ?? one.locale}: ${one.reason}`,
  );
  return {
    message: [prefix + filled, ...failed].join(' · '),
    ok: failed.length === 0,
  };
};

/**
 * One topic: its chips, and when opened, its four names.
 *
 * Each name saves itself when its field is left — there is no Save in the
 * design and nothing here to batch — and the refusal, if there is one, sits
 * under the row it belongs to. Deleting goes through the same question the
 * previous version asked: where do the posts go.
 */
function TopicRow({
  topic,
  others,
  open,
  onToggle,
}: {
  topic: AdminTopic;
  others: AdminTopic[];
  open: boolean;
  onToggle: () => void;
}) {
  const remove = useDeleteTopic();

  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [destination, setDestination] = useState('');

  /**
   * Deletes, or asks where the posts should go.
   *
   * Sent without a destination first, always. The server counts the posts and
   * refuses, and that refusal is what opens the question below — so the rule
   * about what may be deleted lives in one place. Deciding here from `posts`
   * would be a second copy of it, and the one that is wrong the moment another
   * tab files something under this topic.
   */
  const del = (moveTo?: string) => {
    setError(null);
    remove.mutate(
      { id: topic.id, moveTo },
      {
        onError: (failure) => {
          setError(saveMessage(failure));
          if (!moveTo && topic.posts > 0 && others.length > 0) setMoving(true);
        },
      },
    );
  };

  const label = topicName(topic.name);
  const busy = remove.isPending;

  return (
    <div className="border-b py-[9px]" style={{ borderColor: 'var(--line)' }}>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 bg-transparent p-0 text-left"
          style={{ color: 'inherit' }}
        >
          <svg
            aria-hidden="true"
            width="9"
            height="9"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-none transition-transform"
            style={{ color: 'var(--dim)', transform: open ? 'rotate(90deg)' : 'none' }}
          >
            <path d="M4.5 2.5L8 6l-3.5 3.5" />
          </svg>
          <span className="truncate text-[12.5px] font-semibold">{label}</span>
          <span className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
            {topic.slug}
          </span>
        </button>
        <span
          className="flex flex-none gap-[5px]"
          role="group"
          aria-label={`Languages of ${label}`}
        >
          {TOPIC_LOCALES.map((locale) => {
            const has = !!topic.name[locale]?.trim();
            return (
              <span
                key={locale}
                title={`${localeLabels[locale]} · ${has ? 'set' : 'missing'}`}
                data-set={has ? 'true' : 'false'}
                className="rounded-[5px] border px-[5px] py-0.5 font-mono text-[9.5px] tracking-[0.02em]"
                style={{
                  color: has ? 'var(--ok)' : 'var(--dim)',
                  background: has ? 'transparent' : 'var(--in)',
                  borderColor: has ? 'var(--line2)' : 'var(--line)',
                }}
              >
                {locale}
              </span>
            );
          })}
        </span>
        <span className="flex-none font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
          {topic.posts === 0 ? 'No posts' : `${topic.posts} post${topic.posts === 1 ? '' : 's'}`}
        </span>
        {/*
          Offered on every row, including the ones with posts. The server
          refuses those and says how many — which is a more useful answer
          than a greyed button that cannot explain itself.
        */}
        <button
          type="button"
          onClick={() => del()}
          disabled={busy}
          aria-label={`Delete ${label}`}
          title="Delete topic"
          className="focus-ring inline-flex flex-none cursor-pointer bg-transparent px-1 py-0.5 disabled:cursor-default disabled:opacity-50"
          style={{ color: 'var(--dim)' }}
        >
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path
              d="M3.4 4.8h9.2M6.4 4.8V3.2h3.2v1.6M4.6 4.8l.6 8h5.6l.6-8"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {open ? (
        <div className="grid gap-2 pt-2.5 pb-1 pl-[17px] [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
          {TOPIC_LOCALES.map((locale) => (
            <NameField key={locale} topic={topic} locale={locale} />
          ))}
        </div>
      ) : null}

      {error ? (
        <span
          className="mt-1.5 block text-xs leading-[1.55]"
          role="alert"
          style={{ color: 'var(--danger)' }}
        >
          {error}
        </span>
      ) : null}

      {/*
        The design's own words for this: deleting a topic that still has posts
        "asks you to move those posts first". So it asks, rather than stopping
        at the refusal — a dead end would leave somebody re-filing four posts
        by hand to get rid of one topic.
      */}
      {moving ? (
        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          <label className="field-label flex items-center gap-2">
            Move {topic.posts === 1 ? 'it' : 'them'} to
            <select
              aria-label={`Move the posts in ${label} to`}
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
              className="field field-sm"
            >
              <option value="">Choose a topic…</option>
              {others.map((one) => (
                <option key={one.id} value={one.id}>
                  {topicName(one.name)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => del(destination)}
            disabled={!destination || busy}
            className="btn-accent focus-ring min-h-8 cursor-pointer rounded-[9px] px-3 py-1.5 text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {remove.isPending ? 'Moving…' : 'Move and delete'}
          </button>
          <button
            type="button"
            onClick={() => {
              setMoving(false);
              setError(null);
            }}
            className="btn-quiet focus-ring cursor-pointer px-2 py-1 text-[12.5px]"
          >
            Keep it
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One language's name, saved when the field is left.
 *
 * The draft is the field's own until then, so a translation landing from the
 * model while somebody is mid-word does not replace what they are typing;
 * once saved, or when the stored value changes under an untouched field, the
 * field shows what the server holds.
 *
 * Its own mutation and its own error, not the row's. Four fields sharing one
 * `mutate` would hand the callbacks of an earlier save to whichever field
 * saved last — TanStack keeps only the latest — and a field whose "saved"
 * never arrived would sit marked as being edited, refusing the translation
 * that then landed for it.
 */
function NameField({ topic, locale }: { topic: AdminTopic; locale: TopicLocale }) {
  const update = useUpdateTopic();
  const [error, setError] = useState<string | null>(null);

  const stored = topic.name[locale] ?? '';
  const [draft, setDraft] = useState(stored);
  const [dirty, setDirty] = useState(false);
  const [seen, setSeen] = useState(stored);

  /*
   * The stored value moved — a translation landed, or another field's save
   * refetched the list. Follow it unless this field holds an edit that
   * differs from what was stored: a draft equal to the stored text is not
   * an edit, whatever the flag says, and releasing it here is what lets a
   * blanked-then-translated field show the translation.
   */
  if (seen !== stored) {
    // Against what was stored *before* this change: the draft is an edit
    // only if it differs from what the field started from.
    const untouched = !dirty || draft.trim() === seen.trim();
    setSeen(stored);
    if (untouched) {
      setDraft(stored);
      setDirty(false);
    }
  }

  const commit = () => {
    if (!dirty) return;
    // Nothing changed after all: no request, and the draft is released so a
    // later translation can fill the field.
    if (draft.trim() === stored.trim()) {
      setDirty(false);
      setDraft(stored);
      return;
    }
    setError(null);
    update.mutate(
      { id: topic.id, locale, name: draft },
      {
        onSuccess: () => setDirty(false),
        onError: (failure) => setError(saveMessage(failure)),
      },
    );
  };

  const empty = draft.trim().length === 0;
  const label = topicName(topic.name);

  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold">{localeLabels[locale]}</span>
        {empty ? (
          <span className="text-[10px]" style={{ color: 'var(--warn)' }}>
            missing
          </span>
        ) : null}
      </span>
      <input
        aria-label={`${localeLabels[locale]} name of ${label}`}
        value={draft}
        placeholder={locale === 'en' ? '' : 'Not translated'}
        onChange={(event) => {
          setDraft(event.target.value);
          setDirty(true);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
        }}
        className="field min-h-[30px] w-full text-[12.5px]"
        style={{ borderColor: empty ? 'var(--warn)' : undefined }}
      />
      {error ? (
        <span className="text-xs leading-[1.55]" role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </span>
      ) : null}
    </label>
  );
}
